import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

const emptyWarehouse = () => ({
  name: '',
  code: '',
  type: 'warehouse',
  is_active: true,
  city: '',
  country: '',
  postal_code: '',
  fulfillment_priority: 100,
});

/**
 * Create / edit a stock location. `editing=null` means create.
 */
export default function WarehouseDialog({ open, onClose, brandId, editing, onSaved }) {
  const [form, setForm] = useState(emptyWarehouse());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...emptyWarehouse(), ...editing } : emptyWarehouse());
  }, [open, editing]);

  const save = () => {
    setSaving(true);
    const call = editing
      ? API.updateShopWarehouse({ brand_id: brandId, _id: editing._id, patch: form })
      : API.createShopWarehouse({ brand_id: brandId, patch: form });
    call.then(() => { toast.success('Saved'); onSaved && onSaved(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? 'Edit warehouse' : 'New warehouse'}
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
            <TextField
              size='small'
              required
              label='Name'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='Code'
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 16) })}
              placeholder='TOR-1'
              sx={{ flex: 1 }}
            />
            <FormControl size='small' sx={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label='Type'
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <MenuItem value='warehouse'>Warehouse</MenuItem>
                <MenuItem value='store'>Store</MenuItem>
                <MenuItem value='dropship'>Dropship</MenuItem>
                <MenuItem value='virtual'>Virtual / digital</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
            <TextField
              size='small'
              label='City'
              value={form.city || ''}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='Country (ISO-2)'
              value={form.country || ''}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder='CA'
              sx={{ flex: 1 }}
            />
            <TextField
              size='small'
              label='Postal code'
              value={form.postal_code || ''}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px' alignItems='center'>
            <TextField
              size='small'
              type='number'
              label='Fulfillment priority'
              value={form.fulfillment_priority}
              onChange={(e) => setForm({ ...form, fulfillment_priority: Number(e.target.value) })}
              sx={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label='Active'
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || !form.name}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
